import { reportSlugs } from '@app/ccf/company-report/slugs';
import {
  useFormattedSites,
  useGetPeriodOptions,
  useUnitOptions,
} from '@app/ccf/company-report/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAccount, useGetSites } from '@shared/api';
import { useGetOrganizationTimePeriods } from '@shared/api/hooks/ccf/periods';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import {
  useDeletePurchasedFoodAndDrinkItemRecord,
  usePatchPurchasedFoodAndDrinkItemRecord,
} from '@shared/api/hooks/purchased-goods/food-and-drink';
import {
  FoodActivityRecord,
  InputModeEnum,
  PatchedFoodActivityRecordUpdate,
} from '@shared/api/types';
import InputFormField from '@shared/components/form/input-form-field';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { Skeleton } from '@shared/components/ui/skeleton';
import { useToast } from '@shared/components/ui/use-toast';
import { getCurrencySymbol } from '@shared/lib/format';
import sentry from '@shared/services/sentry';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string, z } from 'zod';
import { usePurchasedFoodAndDrinkItem } from '../../purchased-food-and-drink-item-context';

interface PurchasedFoodAndDrinkItemPurchasesEditFormProps {
  foodActivityRecord?: FoodActivityRecord;
  onOpenChange?: (open: boolean) => void;
  numberOfPurchases: number;
}

export const foodAndDrinkPurchasesSchema = object({
  uuid: string().min(1),
  quantity: z.coerce.number().min(0.001),
  timePeriod: string().min(1),
  unit: string().min(1),
  site: string().optional(),
  totalMassOrVolume: z.coerce.number().min(0).optional(),
  supplier: string().optional(),
  totalSpend: z.preprocess(
    (val) => (val === '' ? null : val),
    z.coerce.number().min(0).nullable().optional()
  ),
});

export default function PurchasedFoodAndDrinkItemPurchasesEditForm({
  foodActivityRecord,
  onOpenChange,
  numberOfPurchases,
}: PurchasedFoodAndDrinkItemPurchasesEditFormProps) {
  const { toast } = useToast();
  const { item } = usePurchasedFoodAndDrinkItem();
  const { data: account } = useAccount(true);
  const { itemUnit } = item;
  const activityInputMode =
    foodActivityRecord?.unit.symbol === 'ea'
      ? InputModeEnum.packs
      : InputModeEnum['total-weight'];

  const { mutateAsync: updateFoodRecord, isLoading: isUpdatingFoodRecord } =
    usePatchPurchasedFoodAndDrinkItemRecord();

  const { mutateAsync: deleteFoodRecord, isLoading: isDeletingFoodRecord } =
    useDeletePurchasedFoodAndDrinkItemRecord();

  const {
    data: units,
    isLoading: unitsIsLoading,
    isError: unitsError,
  } = useGetActivityTypeUnits(reportSlugs.foodAndDrink);
  const { data: periods, isLoading: isPeriodsLoading } =
    useGetOrganizationTimePeriods();
  const { data: sites, isLoading: isSitesLoading } =
    useGetSites({
      page: 1,
      page_size: 1000,
    }) || [];

  const formattedSites = useFormattedSites(sites?.results || []);
  const getPeriodOptions = useGetPeriodOptions(periods!);
  const unitOptions = useUnitOptions(units!);

  const form = useForm<z.infer<typeof foodAndDrinkPurchasesSchema>>({
    resolver: zodResolver(foodAndDrinkPurchasesSchema),
  });

  const { setValue, watch, reset, formState } = form;

  const quantity = watch(`quantity`);
  const itemsPerPack = item.itemsPerPack;
  const itemWeight = item.itemMassOrVolume;

  const onSubmit = async (
    data: z.infer<typeof foodAndDrinkPurchasesSchema>
  ) => {
    const updatedTotalWeightData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      unit: data.unit,
      uuid: data.uuid,
      site: data.site ? data.site : undefined,
      totalSpend: data.totalSpend ?? null,
    };

    const updatedPacksData = {
      timePeriod: data.timePeriod,
      quantity: data.quantity,
      uuid: data.uuid,
      site: data.site ? data.site : undefined,
      totalSpend: data.totalSpend ?? null,
    };

    const updateData: PatchedFoodActivityRecordUpdate =
      activityInputMode === InputModeEnum.packs
        ? updatedPacksData
        : updatedTotalWeightData;

    try {
      await updateFoodRecord({
        activityUuid: item.uuid,
        recordUuid: data.uuid,
        data: updateData,
      });

      onOpenChange?.(false);
      toast({
        title: 'Purchase updated',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to update purchase',
        variant: 'destructive',
      });
    }
  };

  const onDelete = async () => {
    try {
      await deleteFoodRecord({
        activityUuid: item.uuid,
        recordUuid: foodActivityRecord!.uuid,
      });
      onOpenChange?.(false);
      toast({
        title: 'Purchase deleted',
        variant: 'success',
      });
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete purchase',
        variant: 'destructive',
      });
    }
  };

  const setTotalWeight = useMemo(() => {
    return (
      quantity: number,
      itemsPerPack: number,
      itemMassOrVolume: number
    ) => {
      setValue(`totalMassOrVolume`, quantity * itemsPerPack * itemMassOrVolume);
    };
  }, [setValue]);

  useEffect(() => {
    if (foodActivityRecord) {
      reset({
        timePeriod: foodActivityRecord.timePeriod.uuid,
        quantity: foodActivityRecord.quantity,
        unit: foodActivityRecord.unit.uuid,
        uuid: foodActivityRecord.uuid,
        totalMassOrVolume: foodActivityRecord.totalMassOrVolume as number,
        site: foodActivityRecord.site?.uuid || '',
        supplier: foodActivityRecord.supplier?.name || '',
        totalSpend: foodActivityRecord.totalSpend ?? undefined,
      });
    }
  }, [foodActivityRecord, reset]);

  useEffect(() => {
    setTotalWeight(quantity, itemsPerPack, itemWeight);
  }, [quantity, itemsPerPack, setTotalWeight, itemUnit, itemWeight]);

  if (unitsIsLoading || isPeriodsLoading || isSitesLoading) {
    return (
      <div className="grid grid-cols-12 items-end gap-2">
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
        <Skeleton className="col-span-3 h-10 rounded-lg" />
      </div>
    );
  }

  if (!units) {
    throw new Error(
      `Failed to load units for ccf food activity rows ${unitsError || 'No data'}`
    );
  }

  if (!periods) {
    throw new Error(
      `Failed to load periods for food activity rows ${isPeriodsLoading || 'No data'}`
    );
  }

  return (
    <Form {...form}>
      <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid grid-cols-12 items-start gap-3">
          <SelectFormField
            name="timePeriod"
            options={getPeriodOptions}
            disabled
            label="Period"
            className="col-span-4"
          />
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            options={formattedSites}
            label="Site"
            disabled={isUpdatingFoodRecord}
            className="col-span-4"
          />
          <InputFormField
            name="supplier"
            label="Supplier"
            disabled
            className="col-span-4"
          />
          {activityInputMode === 'packs' && (
            <InputFormField
              name="quantity"
              label="Packs purchased"
              type="number"
              disabled={isUpdatingFoodRecord}
              className="col-span-4"
              inputClassName="text-right"
            />
          )}
          {activityInputMode === 'packs' && (
            <InputFormField
              name="totalMassOrVolume"
              label="Total weight"
              className="col-span-4"
              disabled
              adornmentRight={item.itemUnit?.symbol}
              inputClassName="text-right"
            />
          )}
          {activityInputMode === 'total-weight' && (
            <>
              <InputFormField
                name="quantity"
                label="Total weight"
                type="number"
                disabled={isUpdatingFoodRecord}
                className="col-span-4"
                inputClassName="text-right"
              />
              <SelectFormField
                name="unit"
                options={unitOptions}
                label="Unit"
                useShortLabel
                disabled={isUpdatingFoodRecord}
                className="col-span-4"
              />
            </>
          )}
          <InputFormField
            name="totalSpend"
            label="Total spend"
            type="number"
            disabled
            className="col-span-4"
            inputClassName="text-right"
            adornmentRight={getCurrencySymbol(account?.defaultCurrency)}
            optional
          />
        </div>
        <div className="flex justify-between">
          <Button
            type="button"
            variant="destructive"
            onClick={() => onDelete()}
            disabled={isUpdatingFoodRecord || numberOfPurchases <= 1}
            loading={isUpdatingFoodRecord || isDeletingFoodRecord}
          >
            Delete purchase
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={isUpdatingFoodRecord}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isUpdatingFoodRecord}
              disabled={isUpdatingFoodRecord || !formState.isDirty}
            >
              {isUpdatingFoodRecord ? 'Updating...' : 'Update'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
